import React from 'react';
import Icon from '../../../components/AppIcon';

const PaymentProgress = ({ currentStep = 1, className = "" }) => {
  const steps = [
    {
      id: 1,
      title: 'Package Selection',
      description: 'Choose your travel package',
      icon: 'MapPin'
    },
    {
      id: 2,
      title: 'Traveler Details',
      description: 'Enter traveler information',
      icon: 'Users'
    },
    {
      id: 3,
      title: 'Payment',
      description: 'Complete secure payment',
      icon: 'CreditCard'
    },
    {
      id: 4,
      title: 'Confirmation',
      description: 'Booking confirmed',
      icon: 'CheckCircle'
    }
  ];

  const getStepStatus = (stepId) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'current';
    return 'upcoming';
  };

  const getStepClasses = (status) => {
    switch (status) {
      case 'completed':
        return {
          container: 'text-success',
          icon: 'bg-success text-success-foreground',
          line: 'bg-success'
        };
      case 'current':
        return {
          container: 'text-primary',
          icon: 'bg-primary text-primary-foreground',
          line: 'bg-border'
        };
      default:
        return {
          container: 'text-muted-foreground',
          icon: 'bg-muted text-muted-foreground',
          line: 'bg-border'
        };
    }
  };

  return (
    <div className={`bg-card rounded-lg border border-border p-6 shadow-soft ${className}`}>
      <h3 className="font-heading font-semibold text-foreground mb-6">Booking Progress</h3>
      
      <div className="space-y-4">
        {steps.map((step, index) => {
          const status = getStepStatus(step.id);
          const classes = getStepClasses(status);
          const isLast = index === steps.length - 1;
          
          return (
            <div key={step.id} className="relative">
              <div className="flex items-center space-x-4">
                {/* Step Icon */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${classes.icon} flex-shrink-0`}>
                  {status === 'completed' ? (
                    <Icon name="Check" size={16} />
                  ) : (
                    <Icon name={step.icon} size={16} />
                  )}
                </div>
                
                {/* Step Content */}
                <div className="flex-1">
                  <div className={`font-medium text-sm ${classes.container}`}>
                    {step.title}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {step.description}
                  </div>
                </div>
                
                {/* Step Status Indicator */}
                {status === 'current' && (
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                    <span className="text-xs font-medium text-primary">In Progress</span>
                  </div>
                )}
                
                {status === 'completed' && (
                  <div className="flex items-center space-x-1">
                    <Icon name="CheckCircle" size={16} className="text-success" />
                    <span className="text-xs font-medium text-success">Completed</span>
                  </div>
                )}
              </div>
              
              {/* Connecting Line */}
              {!isLast && (
                <div className="ml-5 mt-2">
                  <div className={`w-0.5 h-6 ${classes.line}`}></div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Progress Bar */}
      <div className="mt-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-foreground">Overall Progress</span>
          <span className="text-sm text-muted-foreground">
            {Math.round((currentStep / steps.length) * 100)}%
          </span>
        </div>
        <div className="w-full bg-border rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${(currentStep / steps.length) * 100}%` }}
          ></div>
        </div>
      </div>
      
      {/* Estimated Time */}
      <div className="mt-4 p-3 bg-muted/30 rounded-lg">
        <div className="flex items-center space-x-2">
          <Icon name="Clock" size={14} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            Estimated completion time: 2-3 minutes
          </span>
        </div>
      </div>
    </div>
  );
};

export default PaymentProgress;